import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type HomeSectionRow = Database["public"]["Tables"]["home_sections"]["Row"];

export interface HomeSection {
  id: string;
  section_key: string;
  title: string;
  content: HomeSectionRow["content"];
  is_active: boolean;
  sort_order: number;
  updated_at: string;
}

function mapRow(row: HomeSectionRow): HomeSection {
  return {
    id: row.id,
    section_key: row.section_key,
    title: row.title,
    content: row.content,
    is_active: row.is_active,
    sort_order: row.sort_order,
    updated_at: row.updated_at,
  };
}

export function useHomeSections() {
  return useQuery({
    queryKey: ["home-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("home_sections")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      const map = new Map<string, HomeSectionRow["content"]>();
      (data ?? []).forEach(s => map.set(s.section_key, s.content));
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllHomeSections() {
  return useQuery({
    queryKey: ["home-sections-admin"],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("home_sections")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []).map(mapRow);
    },
  });
}

export function useUpdateHomeSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, content, title, is_active }: { id: string; content?: HomeSectionRow["content"]; title?: string; is_active?: boolean }) => {
      const updates: Database["public"]["Tables"]["home_sections"]["Update"] = {};
      if (content !== undefined) updates.content = content;
      if (title !== undefined) updates.title = title;
      if (is_active !== undefined) updates.is_active = is_active;
      const { error } = await supabase
        .from("home_sections")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["home-sections"] });
      qc.invalidateQueries({ queryKey: ["home-sections-admin"] });
    },
    onError: (e: any) => toast.error("Erro ao salvar seção: " + e.message),
  });
}
